import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import axios from 'axios';
import API from './config';

import Login               from './pages/Login';
import Register            from './pages/Register';
import VerifyEmail         from './pages/VerifyEmail';
import Dashboard           from './pages/Dashboard';
import Markets             from './pages/Markets';
import Deposit             from './pages/Deposit';
import Leaderboard         from './pages/Leaderboard';
import Admin               from './pages/Admin';
import Withdraw            from './pages/Withdraw';
import Terms               from './pages/Terms';
import MarketPage          from './pages/MarketPage';
import Profile             from './pages/Profile';
import PrivacyPolicy       from './pages/PrivacyPolicy';
import ResponsibleGambling from './pages/ResponsibleGambling';
import NotFound            from './pages/NotFound';
import GoogleAuthSuccess from './pages/GoogleAuthSuccess';
import { HelmetProvider }  from 'react-helmet-async';
import SetupNickname from './pages/SetupNickname';
import Investors from './pages/Investors';
import About     from './pages/About';
import Blog      from './pages/Blog';
import HowItWorks from './pages/HowItWorks';
import ChamaMode from './pages/Chamamode';
// ── Protected route — requires login AND verified email ───────────────────────
const ProtectedRoute = ({ children }) => {
  const { token, user } = useAuth();
  if (!token)               return <Navigate to="/login"        replace />;
  if (!user?.is_verified)   return <Navigate to="/verify-email" replace />;
  return children;
};

// ── Admin route — requires login AND is_admin ─────────────────────────────────
const AdminRoute = () => {
  const { token, user } = useAuth();
  if (!token)          return <Navigate to="/login"     replace />;
  if (!user?.is_admin) return <Navigate to="/dashboard" replace />;
  return <Admin />;
};

// ── Maintenance page ──────────────────────────────────────────────────────────
const MaintenancePage = () => {
  const { T } = useTheme();
  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans',sans-serif", color: T.text, padding: 24 }}>
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 24 }}>
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
      </svg>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 10, letterSpacing: '-0.5px' }}>Under Maintenance</h1>
      <p style={{ color: T.textMuted, fontSize: 15, textAlign: 'center', maxWidth: 400, lineHeight: 1.7, marginBottom: 8 }}>
        Mino is currently undergoing scheduled maintenance.
      </p>
      <p style={{ color: T.textDim, fontSize: 13 }}>We'll be back shortly.</p>
    </div>
  );
};

// ── App content ───────────────────────────────────────────────────────────────
function AppContent() {
  const [maintenance, setMaintenance] = useState(false);
  const [checking, setChecking]       = useState(true);
  const { token, user }               = useAuth();

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (!savedTheme || savedTheme === 'light') {
      document.body.classList.add('light');
    } else {
      document.body.classList.remove('light');
    }

    // Render instantly from cache so Router mounts without waiting
    const cached = localStorage.getItem('maintenance');
    if (cached !== null) {
      setMaintenance(cached === 'true');
      setChecking(false);
    }

    axios.get(`${API}/api/settings/public/maintenance`)
      .then(r => {
        setMaintenance(r.data.maintenance);
        localStorage.setItem('maintenance', String(r.data.maintenance));
      })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  if (checking) return null;

  return (
    <Router>
      {maintenance && !user?.is_admin ? <MaintenancePage /> : <Routes>
        {/* Public routes */}
        <Route path="/"           element={<Markets />} />
        <Route path="/markets"    element={<Markets />} />
        <Route path="/markets/:id" element={<MarketPage />} />
        <Route path="/login"      element={<Login />} />
        <Route path="/register"   element={<Register />} />
        <Route path="/terms"      element={<Terms />} />
        <Route path="/privacy"    element={<PrivacyPolicy />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/responsible-gambling" element={<ResponsibleGambling />} />
        <Route path="/auth/google/success" element={<GoogleAuthSuccess />} /> 
        <Route path="/setup-nickname" element={<SetupNickname />} />
        <Route path="/investors" element={<Investors />} />
        <Route path="/about"     element={<About />} />
<Route path="/blog/*" element={<Blog />} />
<Route path="/how-it-works" element={<HowItWorks />} />



        {/* Verification route — token required, is_verified NOT required */}
        <Route path="/verify-email" element={<VerifyEmail />} />

        {/* Protected routes — redirect to /login if no token, /verify-email if unverified */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/deposit"   element={<ProtectedRoute><Deposit /></ProtectedRoute>} />
        <Route path="/withdraw"  element={<ProtectedRoute><Withdraw /></ProtectedRoute>} />
        <Route path="/profile"   element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/chama/*" element={<ProtectedRoute><ChamaMode /></ProtectedRoute>} />

        {/* Admin route — checks token AND is_admin */}
        <Route path="/admin" element={<AdminRoute />} />

        <Route path="*" element={<NotFound />} />
      </Routes>}
    </Router>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}